import {
  Button,
  Collapse,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DateWithClear from "Components/UI/Form/DateWithClear";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const checkDebetCredit = (debet, credit) => {
  if (credit > 0 && debet > 0) return false;
  if (credit < 0 || debet < 0) return false;
  return true;
};

const CreateBooksForm = ({
  clients,
  accounts,
  setEnableSaveButton,
  onDataChange,
  initFormData,
}) => {
  const [rowCounter, setRowCounter] = useState(3); // Default amount of rows
  const [data, setData] = useState(initFormData);
  const [rowErrors, setRowErrors] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const onRowChange = (rowId, field, rowData) => {
    let newData = { ...data };
    newData.rows = [
      ...data.rows.map((row) =>
        row.id === rowId ? { ...row, [field]: rowData } : row
      ),
    ];
    setData(newData);
    onDataChange(newData);
  };

  const onFieldsChange = (field, fieldData) => {
    const newData = { ...data, [field]: fieldData };
    setData(newData);
    onDataChange(newData);
  };

  useEffect(() => {
    let errors = 0;

    const newTotAmount = data.rows.reduce((acc, row) => {
      if (!checkDebetCredit(row.debet, row.credit)) {
        errors = errors + 1;
      }

      return parseFloat(
        (acc + (row.debet || 0) - (row.credit || 0)).toFixed(4)
      );
    }, 0);

    setRowErrors(errors);
    setTotalAmount(newTotAmount);
    setEnableSaveButton(rowErrors === 0 && newTotAmount === 0);
  }, [data.rows, rowErrors, setEnableSaveButton]);

  const removeRow = (id) => {
    let newData = { ...data };

    newData.rows = newData.rows.filter((row) => row.id !== id);
    setData(newData);
    onDataChange(newData);
  };

  const addRow = () => {
    let newData = { ...data };
    newData.rows = [
      ...newData.rows,
      {
        id: rowCounter,
        accountId: "",
        name: "",
        debet: null,
        credit: null,
      },
    ];
    setData(newData);
    onDataChange(newData);

    setRowCounter((prevCounter) => prevCounter + 1);
  };

  return (
    <Grid item container>
      <Grid item container>
        <Grid item container spacing={3} md={12} mt={1}>
          <Grid item md={4} xs={12}>
            <FormControl margin="dense" fullWidth sx={{ textAlign: "left" }}>
              <InputLabel>Klient</InputLabel>
              <Select
                name="Klient"
                size="small"
                value={data.clientId}
                onChange={(e) => onFieldsChange("clientId", e.target.value)}
                label="Klient"
              >
                {clients
                  ?.sort((a, b) => a.Title.localeCompare(b.Title))
                  .map((client) => (
                    <MenuItem key={client.Id} value={client.Id}>
                      {client.Title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item md={4} xs={12}>
            <TextField
              size="small"
              margin="dense"
              fullWidth
              label="Beskrivning"
              value={data.comment}
              onChange={(e) => onFieldsChange("comment", e.target.value)}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <DateWithClear
              label="Bokföringsdatum"
              value={dayjs(data.date)}
              onChange={(e) =>
                onFieldsChange("date", dayjs(e).format("YYYY-MM-DD"))
              }
            />
          </Grid>
        </Grid>
        <Grid
          item
          container
          border={"1px solid lightgray"}
          borderRadius={"10px"}
          marginTop={2}
        >
          <Columns hasRows={data.rows.length > 0} />
          {data.rows
            .sort((a, b) => a.id - b.id)
            .map((row, index) => (
              <Row
                key={index}
                data={row}
                accounts={accounts}
                onChange={(field, data) => onRowChange(row.id, field, data)}
                isLastChild={index === data.rows.length - 1}
                removeRow={() => removeRow(row.id)}
              />
            ))}
        </Grid>
      </Grid>

      <Grid item md={12} mt={1} mb={1}>
        <Button variant="contained" fontSize="large" fullWidth onClick={addRow}>
          <AddIcon />
        </Button>
      </Grid>

      <Collapse sx={{ width: "100%" }} in={totalAmount !== 0}>
        <Typography textAlign="right" color="error">
          Summan av Debet och Kredit blir inte 0.
        </Typography>
      </Collapse>
    </Grid>
  );
};

const Row = ({ accounts, data, onChange, isLastChild, removeRow }) => {
  const sharedGridStyles = (hasLeftBorder) => ({
    borderBottom: !isLastChild ? "1px solid lightgray" : "none",
    borderLeft: hasLeftBorder ? "1px solid lightgray" : "none",
    textAlign: "left",
  });

  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: "none",
      },
    },
  };

  return (
    <Grid item container>
      {/* SELECT ACCOUNT */}
      <Grid item md={4} xs={3} sx={sharedGridStyles(false)}>
        <FormControl required fullWidth sx={{ textAlign: "left" }}>
          <Select
            name="Konto"
            value={data.accountId}
            variant="outlined"
            onChange={(e) => onChange("accountId", e.target.value)}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
          >
            {accounts
              ?.sort((a, b) => a.AccountNumber.localeCompare(b.AccountNumber))
              .map((account) => (
                <MenuItem key={account.Id} value={account.Id}>
                  {account.Title}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>

      {/* BENÄMNING */}
      <Grid item md={4} xs={4} sx={sharedGridStyles(true)}>
        <TextField
          fullWidth
          value={data.name}
          onChange={(e) => onChange("name", e.target.value)}
          variant="outlined"
          sx={textFieldStyle}
          size="small"
        />
      </Grid>

      {/* DEBET */}
      <Grid item md={1.75} xs={2} sx={sharedGridStyles(true)}>
        <TextField
          placeholder="0"
          fullWidth
          variant="outlined"
          value={data.debet}
          onChange={(e) => onChange("debet", Number(e.target.value))}
          sx={{
            ...textFieldStyle,
            "& .MuiInputBase-input": {
              textAlign: "right",
            },
            "& .MuiFormHelperText-root": {
              textAlign: "right",
            },
          }}
          type="number"
          size="small"
          error={!checkDebetCredit(data.debet, data.credit)}
          helperText={
            !checkDebetCredit(data.debet, data.credit)
              ? "Får ej ha kredit och debet"
              : ""
          }
        />
      </Grid>

      {/* CREDIT */}
      <Grid item md={1.75} xs={2} sx={sharedGridStyles(true)}>
        <TextField
          placeholder="0"
          fullWidth
          variant="outlined"
          value={data.credit}
          onChange={(e) => onChange("credit", Number(e.target.value))}
          sx={{
            ...textFieldStyle,
            "& .MuiInputBase-input": {
              textAlign: "right",
            },
            "& .MuiFormHelperText-root": {
              textAlign: "right",
            },
          }}
          type="number"
          error={!checkDebetCredit(data.debet, data.credit)}
          helperText={
            !checkDebetCredit(data.debet, data.credit)
              ? "Får ej ha kredit och debet"
              : ""
          }
          size="small"
        />
      </Grid>
      <Grid
        item
        md={0.5}
        xs={1}
        sx={sharedGridStyles(true)}
        justifyContent={"center"}
      >
        <Button fullWidth color="error" onClick={removeRow}>
          <RemoveIcon />
        </Button>
      </Grid>
    </Grid>
  );
};

const Columns = (hasRows) => {
  const gridWidths = [
    { md: 4, xs: 3, align: "left", label: "Konto" },
    { md: 4, xs: 4, align: "left", label: "Benämning" },
    { md: 1.75, xs: 2, align: "right", label: "Debet" },
    { md: 1.75, xs: 2, align: "right", label: "Kredit" },
    { md: 0.5, xs: 1, align: "right", label: "" },
  ];
  return (
    <Grid container>
      {gridWidths.map((item, index) => (
        <Grid
          key={index}
          item
          md={item.md}
          xs={item.xs}
          borderBottom={hasRows ? "1px solid lightgray" : "none"}
          borderLeft={index > 0 ? "1px solid lightgray" : "none"}
          textAlign={item.align}
          p={1}
        >
          <Typography fontWeight="bold">{item.label}</Typography>
        </Grid>
      ))}
    </Grid>
  );
};

export default CreateBooksForm;
